import { Grid, InputLabel, Select, MenuItem, FormControl, Typography, Box } from "@mui/material";
import ReportIcon from "@mui/icons-material/Report";

import { useState, useEffect } from "react";
import { candidateRequests } from "api/candidateApi";
import {
  required,
  useNotify,
  useRefresh,
  SelectInput,
  ReferenceInput,
  ArrayInput,
  SimpleFormIterator,
  FormDataConsumer,
} from "react-admin";
import { settings } from "app";
import FormModal from "server/components/Modals/FormModal";
import LoadingActionButton from "server/components/LoadingActionButton";
import AddIcon from "@mui/icons-material/Add";
import { useFormContext } from "react-hook-form";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";

const ChangeSpecialityDialog = ({ candidacyId, disabled }) => {
  const notifyError = useNotifyError();
  const notify = useNotify();
  const refresh = useRefresh();

  const [avaliableForUpdate, setAvaliableForEdit] = useState(null);

  const [selectedSpecialityIds, setSelectedSpecialityIds] = useState(null);

  const [scoreLineOptions, setScoreLineOptions] = useState([]);
  const [components, setComponents] = useState([]);

  const checkPossibleChange = async () => {
    try {
      const { data } = await dataProvider.customRequest(candidateRequests.getCandidacySpecialitiesForAdd(candidacyId));
      const hasData = data.length > 0;
      setAvaliableForEdit(hasData);
    } catch (err) {
      notifyError(err);
      setAvaliableForEdit(false);
      return false;
    }
  };

  const handleSelectedSpecialityIds = data => {
    if (!data) {
      setSelectedSpecialityIds(null);

      return;
    }
    const ids = data.map(p => p?.specialityId).filter(Boolean);
    setSelectedSpecialityIds(ids);
  };

  //set changes of scoreline
  const handleChangeScoreLineComponents = ({ specialityId, lineId, manualPickComponents }) => {
    const lineKey = String(lineId);
    setComponents(prev => {
      if (!selectedSpecialityIds || !selectedSpecialityIds.includes(specialityId)) {
        return prev.filter(item => item.specialityId !== specialityId);
      }

      const existingIndex = prev.findIndex(x => x.specialityId === specialityId);

      if (existingIndex !== -1) {
        const updated = [...prev];
        const existing = updated[existingIndex];

        updated[existingIndex] = {
          ...existing,
          values: {
            ...existing.values,
            [lineKey]: manualPickComponents === "" ? undefined : manualPickComponents,
          },
        };

        const cleanedValues = Object.entries(updated[existingIndex].values).reduce((acc, [k, v]) => {
          if (v !== undefined && v !== "") acc[k] = v;
          return acc;
        }, {});

        updated[existingIndex] = {
          ...updated[existingIndex],
          values: cleanedValues,
        };

        return updated;
      }

      return [
        ...prev,
        {
          specialityId,
          values: {
            [lineKey]: manualPickComponents === "" ? undefined : manualPickComponents,
          },
        },
      ];
    });
  };

  // fetchScoreLine
  useEffect(() => {
    const specialityIds = selectedSpecialityIds;
    if (!specialityIds || specialityIds.length === 0) {
      setComponents([]);
      setScoreLineOptions([]);
      return;
    }

    const fetchScoreLineData = async () => {
      try {
        const data = await Promise.all(
          specialityIds.map(async specialityId => {
            if (!specialityId) return null;

            const res = await dataProvider.customRequest(
              candidateRequests.getCandidacySpecialityManualPickComponents(candidacyId, specialityId),
            );

            const savedData = Object.entries(res.data).map(item => {
              return { lineId: Number(item[0]), options: item[1] };
            });

            return {
              specialityId,
              scoreLineItems: savedData,
            };
          }),
        );

        const filteredResults = data.filter(Boolean);
        setScoreLineOptions(filteredResults);
      } catch (err) {
        notifyError(err);
        setScoreLineOptions([]);
      }
    };

    fetchScoreLineData();
  }, [selectedSpecialityIds, candidacyId, notifyError]);

  const [data, setData] = useState(null);

  const handleSubmit = async () => {
    const grouped = {};
    components.forEach(item => {
      if (item.values && Object.keys(item.values).length > 0) {
        grouped[item.specialityId] = { ...item.values };
      }
    });
    for (const speciality of scoreLineOptions) {
      const specialityId = speciality.specialityId;

      const expectedCount = speciality.scoreLineItems.length;

      const actualCount = Object.keys(grouped[specialityId] || {}).length;

      if (actualCount !== expectedCount) {
        notify(`Моля, изберете всички балообразуващи компоненти (${actualCount}/${expectedCount}) за специалност.`, {
          type: "error",
        });
        return false;
      }
    }
    const payload =
      Object.entries(grouped).length > 0
        ? Object.entries(grouped).map(([specialityId, lineMap]) => ({
            specialityId: Number(specialityId),
            manualPickComponents: { ...lineMap },
          }))
        : data;

    if (!payload || payload.length === 0) {
      notify("Няма направени промени", { type: "warning" });
      return;
    }

    try {
      await dataProvider.customRequest(candidateRequests.addCandidacySpeciality(candidacyId), payload[0], "POST");
      notify("Успешна промяна", { type: "success" });
      refresh();
      setComponents([]);
      setScoreLineOptions([]);
    } catch (err) {
      setComponents([]);
      setScoreLineOptions([]);
      notifyError(err);
      return false;
    }
  };
  const SpecialityContent = ({ setData }) => {
    const { watch } = useFormContext();
    const specialities = watch("specialitiesAdd");

    useEffect(() => {
      setData(specialities);
    }, [specialities, setData]);

    return avaliableForUpdate ? (
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
        >
          <ArrayInput
            source="specialitiesAdd"
            label=" "
            defaultValue={[{}]}
          >
            <SimpleFormIterator
              disableReordering
              disableAdd
              disableRemove
              disableClear
              sx={settings.style.iteratorWithoutGap}
            >
              <FormDataConsumer>
                {({ formData, scopedFormData }) => {
                  return (
                    <Grid
                      container
                      spacing={2}
                    >
                      <Grid
                        item
                        xs={12}
                        md={6}
                      >
                        <ReferenceInput
                          source="specialityId"
                          reference={candidateRequests.getCandidacySpecialitiesForAdd(candidacyId)}
                          filter={{ excludeIds: selectedSpecialityIds }}
                        >
                          <SelectInput
                            onChange={e => handleSelectedSpecialityIds(formData?.specialitiesAdd)}
                            label="Специалност"
                            optionText="nameBg"
                            validate={[required()]}
                            resettable
                          />
                        </ReferenceInput>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={6}
                      >
                        {scoreLineOptions?.map(item =>
                          item.scoreLineItems?.map(scoreLine => {
                            return (
                              item.specialityId === scopedFormData?.specialityId && (
                                <FormControl
                                  fullWidth
                                  key={`${item.specialityId}-${scoreLine.lineId}`}
                                >
                                  <InputLabel id={`componentId-${item.specialityId}-${scoreLine.lineId}`}>
                                    Балообразуващ компонент
                                  </InputLabel>
                                  <Select
                                    fullWidth
                                    labelId={`componentId-${item.specialityId}-${scoreLine.lineId}`}
                                    id={`componentId-${item.specialityId}-${scoreLine.lineId}`}
                                    value={
                                      components.find(x => x.specialityId === item.specialityId)?.values?.[
                                        String(scoreLine.lineId)
                                      ] || ""
                                    }
                                    onChange={e => {
                                      const newVal = e.target.value === "" ? "" : Number(e.target.value);
                                      handleChangeScoreLineComponents({
                                        specialityId: item.specialityId,
                                        lineId: scoreLine.lineId,
                                        manualPickComponents: newVal,
                                      });
                                    }}
                                    sx={{
                                      height: "3.5rem",
                                      width: "100%",
                                    }}
                                  >
                                    <MenuItem value={""}></MenuItem>
                                    {scoreLine?.options?.map((option, i) => (
                                      <MenuItem
                                        key={`componentId-${item.specialityId}-${scoreLine.lineId}-${i}`}
                                        value={option.id}
                                      >
                                        {option.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )
                            );
                          }),
                        )}
                      </Grid>
                    </Grid>
                  );
                }}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      </Grid>
    ) : (
      <Box sx={{ margin: "auto" }}>
        <Box sx={{ display: "flex", gap: 1, width: "fit-content", margin: "2rem auto" }}>
          <ReportIcon
            color="error"
            fontSize="large"
            textAlign="center"
          />
          <Typography
            fontSize={14}
            sx={{ margin: "auto", display: "inline" }}
            variant="body1"
            color={"error"}
          >
            Невалидно действие
          </Typography>
        </Box>
        <Typography
          textAlign={"center"}
          margin={"2rem auto"}
          fontSize={14}
        >
          Няма налична специалност, която да добавите!
        </Typography>
      </Box>
    );
  };

  return (
    <FormModal
      resetProps={() => {
        setComponents([]);
        setScoreLineOptions([]);
      }}
      hideSaveButton={!avaliableForUpdate}
      dialogProps={{
        sx: {
          "& .MuiDialog-paper": {
            textAlign: "center",
            maxHeight: "80%",
            minWidth: "70%",
            minHeight: "40%",
          },
        },
      }}
      titleContent={
        <Typography
          fontSize={"large"}
          fontWeight={"bold"}
          textAlign={"center"}
        >
          <AddIcon sx={{ verticalAlign: "middle", marginRight: ".5rem", marginBottom: ".2rem" }} /> Добави специалност
        </Typography>
      }
      onConfirm={handleSubmit}
      onOpen={checkPossibleChange}
      triggerButton={
        <LoadingActionButton
          label="Добави специалност"
          icon={<AddIcon />}
          sx={{ mt: 2 }}
          disabled={disabled}
        />
      }
    >
      <SpecialityContent setData={setData} />
    </FormModal>
  );
};

export default ChangeSpecialityDialog;
